/** A stop's activity rows in the inspector (plan §5): compact rows ("Cliffs of Moher
 *  entry · from 12.00 per person · on the map"), one expanded at a time with its fields.
 *  In edit mode each row has its actions in one menu — one tab stop (up / down, move to
 *  another stop, remove), the
 *  saved rows the draft removed stay struck through with Restore until the save, and
 *  "Add an activity" closes the list (its room kept in view mode).
 *
 *  Documents are not part of the draft: "Documents now" (an immediate action) manages a
 *  SAVED row's on-trip documents at once; a new row says "Save first to attach documents". */
import AddIcon from '@mui/icons-material/Add';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import { Box, Button, ButtonBase, IconButton, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import { memo, useCallback, useEffect, useRef, useState } from 'react';

import { useDraftStable } from '@/features/records/draft/DraftProvider';
import { CHANGED_TINT } from '@/features/records/fields/FieldSlot';
import { RestoreButton, StateMarks, stateWords } from '@/features/records/fields/RowMarks';
import { useRecordIssues } from '@/features/records/outline/issues';
import { useDraftSelector, useOutlineField, useOutlineNode } from '@/features/records/outline/store';

import { useTourRecord } from '../record/TourRecordContext';

import { DocumentsNow } from './DocumentsNow';
import { ItineraryFieldSlot, type FieldSpec } from './fields';
import { activityRec, itineraryTree, newRowName, SLOT_ACTIVITIES, slotRec } from './model';
import { useItinerary } from './store';
import {
  addActivity,
  moveActivity,
  moveActivityToSlot,
  removeActivity,
  removedActivities,
  restoreActivity,
  slotName,
} from './structure';
import { useStructure } from './useStructure';

export const ACTIVITY_ROW_PX = 44;

/** Module constants: a field's props stay equal across renders (its memo holds). */
const SPEC = {
  activity: { kind: 'picker', picker: 'activities', errorText: 'Couldn’t load the operator’s activities' },
  text256: { kind: 'text', maxLength: 256 },
  prefix: { kind: 'text', maxLength: 64, placeholder: 'from' },
  unit: { kind: 'text', maxLength: 64, placeholder: 'per person' },
  price: { kind: 'price' },
  currency: { kind: 'picker', picker: 'currencies', errorText: 'Couldn’t load the currencies' },
  onMap: { kind: 'switch', on: 'Shown', off: 'Not shown' },
} as const satisfies Record<string, FieldSpec>;

export const ActivityList = memo(function ActivityList({ slotId, columns }: { slotId: number; columns: number }) {
  const { editing, canEdit, baseline, tourRec, expand } = useItinerary();
  const live = editing && canEdit;
  // The list only (a keystroke in the stop's title does not render the rows).
  const ids = useOutlineField(slotRec(slotId), SLOT_ACTIVITIES).value;
  const list = Array.isArray(ids) ? (ids as number[]) : [];
  // Saved rows the draft took out of this stop (not moved to another stop).
  const removed = useDraftSelector(
    (entries) => {
      if (!entries) return [] as number[];
      const placed = new Set(itineraryTree(entries, baseline.paths, tourRec).flatMap((d) => d.slots.flatMap((s) => s.activities)));
      return removedActivities(entries, baseline.paths, slotId, placed);
    },
    (v) => v.join(','),
  );
  const [expanded, setExpanded] = useState<number | null>(null);
  const onToggle = useCallback((id: number, open: boolean) => setExpanded(open ? id : null), []);
  // A ledger row for one of these rows' fields opens it.
  const listRef = useRef(list);
  listRef.current = list;
  useEffect(() => expand.listen((id) => listRef.current.includes(id) && setExpanded(id)), [expand]);
  const run = useStructure();

  return (
    <Box component="section" aria-label="Activities" data-itin-activities sx={{ display: 'grid', gap: 0.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
        Activities ({list.length})
      </Typography>
      {list.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ minHeight: ACTIVITY_ROW_PX, display: 'flex', alignItems: 'center' }}>
          No activities in this stop.
        </Typography>
      )}
      {list.map((id, i) => (
        <ActivityItem
          key={id}
          activityId={id}
          slotId={slotId}
          index={i}
          count={list.length}
          expanded={expanded === id}
          onToggle={onToggle}
          columns={columns}
          removed={false}
        />
      ))}
      {removed.map((id) => (
        <ActivityItem key={`x${id}`} activityId={id} slotId={slotId} index={-1} count={list.length} expanded={false} onToggle={onToggle} columns={columns} removed />
      ))}
      <Box data-itin-add="activity" sx={{ minHeight: 40, display: 'flex', alignItems: 'center' }}>
        {live && (
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => {
              const id = run((c) => addActivity(c, slotId));
              if (typeof id === 'number') setExpanded(id);
            }}
          >
            Add an activity
          </Button>
        )}
      </Box>
    </Box>
  );
});

function priceSummary(prefix: unknown, min: unknown, max: unknown, unit: unknown): string {
  const t = (v: unknown) => (typeof v === 'string' ? v.trim() : '');
  const range = t(min) && t(max) ? `${t(min)}–${t(max)}` : t(min) || t(max);
  return [t(prefix), range, t(unit)].filter(Boolean).join(' ');
}

export const ActivityItem = memo(function ActivityItem({
  activityId,
  slotId,
  index,
  count,
  expanded,
  onToggle,
  columns,
  removed,
}: {
  activityId: number;
  slotId: number;
  index: number;
  count: number;
  expanded: boolean;
  onToggle: (id: number, open: boolean) => void;
  columns: number;
  removed: boolean;
}) {
  const { editing, canEdit, issues } = useItinerary();
  const live = editing && canEdit;
  const rec = activityRec(activityId);
  const node = useOutlineNode(rec);
  const recIssues = useRecordIssues(issues, rec);
  const { names } = useTourRecord();
  const run = useStructure();
  const activity = node.value('activity.activity');
  const display = node.value('activity.display_name');
  const name =
    (typeof display === 'string' && display.trim()) ||
    (typeof activity === 'number' ? names.activityLabel(activity) : activityId < 0 ? `${newRowName(activityId)} — choose an activity` : 'No activity');
  const price = priceSummary(node.value('activity.cost_prefix'), node.value('activity.cost_min'), node.value('activity.cost_max'), node.value('activity.cost_unit'));
  const onMap = node.value('activity.display_on_map') === true;
  const changed = editing && node.entries !== undefined;
  const invalid = recIssues ? (Object.values(recIssues).flatMap((p) => p.errors)[0] ?? null) : null;
  const [moveAnchor, setMoveAnchor] = useState<HTMLElement | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  return (
    <Box
      data-itin-activity={activityId}
      data-removed={removed || undefined}
      sx={[
        { border: 1, borderColor: invalid ? 'error.main' : 'divider', borderRadius: 1 },
        (changed || removed) && { bgcolor: CHANGED_TINT },
      ]}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minHeight: ACTIVITY_ROW_PX, px: 0.5 }}>
        <ButtonBase
          onClick={() => !removed && onToggle(activityId, !expanded)}
          disabled={removed}
          aria-expanded={removed ? undefined : expanded}
          aria-label={`${name}${price ? `, ${price}` : ''}${onMap ? ', on the map' : ''}${stateWords({ changed, invalid, removed })}`}
          sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 0.75, justifyContent: 'flex-start', textAlign: 'left', borderRadius: 1, py: 0.5, px: 0.5 }}
        >
          {removed ? null : expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          <Typography variant="body2" noWrap sx={{ minWidth: 0, fontWeight: 500, textDecoration: removed ? 'line-through' : undefined }}>
            {name}
          </Typography>
          {price && (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 'none', textDecoration: removed ? 'line-through' : undefined }}>
              {price}
            </Typography>
          )}
          {onMap && (
            <Tooltip title="Shown on the map">
              <PlaceOutlinedIcon aria-hidden sx={{ fontSize: 16, color: 'text.secondary', flex: 'none' }} />
            </Tooltip>
          )}
        </ButtonBase>
        <StateMarks changed={changed && !removed} invalid={invalid} />
        {/* The actions' room is kept in view mode. */}
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 'none', minWidth: 72, justifyContent: 'flex-end' }}>
          {live && removed && (
            <RestoreButton name={name} hint="keep this activity row" onRestore={() => run((c) => restoreActivity(c, activityId, slotId))} />
          )}
          {live && !removed && (
            // One tab stop: the row's actions in a menu.
            <>
              <Tooltip title="Actions">
                <IconButton size="small" aria-label={`Actions for ${name}`} aria-haspopup="menu" onClick={(e) => setMenuAnchor(e.currentTarget)}>
                  <MoreVertIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
              <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
                <MenuItem disabled={index <= 0} onClick={() => { setMenuAnchor(null); run((c) => moveActivity(c, activityId, -1)); }}>
                  Move up
                </MenuItem>
                <MenuItem disabled={index >= count - 1} onClick={() => { setMenuAnchor(null); run((c) => moveActivity(c, activityId, 1)); }}>
                  Move down
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    const anchor = menuAnchor;
                    setMenuAnchor(null);
                    setMoveAnchor(anchor);
                  }}
                >
                  Move to another stop…
                </MenuItem>
                <MenuItem onClick={() => { setMenuAnchor(null); run((c) => removeActivity(c, activityId)); }} sx={{ color: 'error.main' }}>
                  Remove
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>
      </Box>
      {moveAnchor && <MoveToStopMenu anchor={moveAnchor} activityId={activityId} slotId={slotId} onClose={() => setMoveAnchor(null)} />}
      {expanded && !removed && (
        <Box sx={{ px: 1, pb: 1, display: 'grid', columnGap: 2, gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
          <ItineraryFieldSlot rec={rec} path="activity.activity" spec={SPEC.activity} wide />
          <ItineraryFieldSlot rec={rec} path="activity.display_name" spec={SPEC.text256} wide />
          <ItineraryFieldSlot rec={rec} path="activity.cost_prefix" spec={SPEC.prefix} />
          <ItineraryFieldSlot rec={rec} path="activity.cost_unit" spec={SPEC.unit} />
          <ItineraryFieldSlot rec={rec} path="activity.cost_min" spec={SPEC.price} />
          <ItineraryFieldSlot rec={rec} path="activity.cost_max" spec={SPEC.price} />
          <ItineraryFieldSlot rec={rec} path="activity.currency" spec={SPEC.currency} />
          <ItineraryFieldSlot rec={rec} path="activity.display_on_map" spec={SPEC.onMap} />
          <Box sx={{ gridColumn: '1 / -1', minHeight: 40, display: 'flex', alignItems: 'center' }}>
            <DocumentsNow activityId={activityId} />
          </Box>
        </Box>
      )}
    </Box>
  );
});

/** The stops to move an activity row to, read from the draft when the menu opens. */
function MoveToStopMenu({ anchor, activityId, slotId, onClose }: { anchor: HTMLElement; activityId: number; slotId: number; onClose: () => void }) {
  const { baseline, tourRec } = useItinerary();
  const { store } = useDraftStable();
  const { names } = useTourRecord();
  const run = useStructure();
  const [targets] = useState(() => {
    const state = store.getState();
    const ctx = { state, baselines: baseline.paths, tourRec, names };
    return itineraryTree(state.entries, baseline.paths, tourRec).flatMap((d, di) =>
      d.slots.map((s) => {
        const time = state.entries[slotRec(s.id)]?.['slot.time']?.to ?? baseline.paths[slotRec(s.id)]?.['slot.time'];
        return { id: s.id, label: `Day ${di + 1} · ${typeof time === 'string' ? `${time} ` : ''}${slotName(ctx, s.id)}` };
      }),
    );
  });
  return (
    <Menu anchorEl={anchor} open onClose={onClose}>
      {targets.map((t) => (
        <MenuItem
          key={t.id}
          disabled={t.id === slotId}
          onClick={() => {
            onClose();
            run((c) => moveActivityToSlot(c, activityId, t.id));
          }}
        >
          {t.label}
          {activityId > 0 && t.id < 0 ? ' — save it first' : ''}
        </MenuItem>
      ))}
    </Menu>
  );
}
